import React, { useCallback, useEffect, useRef, useState } from "react";
import * as AdaptiveCards from "adaptivecards";
import * as markdownit from "markdown-it";
import { Card, Flex, ProviderConsumer as FluentUIThemeConsumer, CardProps, } from "@fluentui/react-northstar";
import { blankPayloadData } from "./AdaptiveCardDesigner";
import { Input, Action } from "adaptivecards";
import { mergeStyleSets } from "@fluentui/react";
import { themeNames } from "@fluentui/react-teams";
import 'github-markdown-css';
import { appState, useSetState } from "../AppState";
import { createCustomElements, CreateCustomElementsProps } from "./CustomComponents/Utils/CustomElementUtils";
import { ACFeatureStates, loadFeatureVars, registerCardCustomComponents } from "./CustomComponents/Utils/CustomRegistration";
import { FileProps } from "../shared/components/EditTicketAttachments/EditTicketAttachments";
import { platformService } from "../shared/services/platform.service";
import { graphService } from "../shared/services/graph.service";
import { ThemeColorScheme } from "../shared/common/TeamsTheme";
import { ReloadEndUserCFOptions } from "../tikit/Request/Requests";
import { UploadFile } from "./CustomComponents/UploadFile";

const getClassNames = (isDisabled: boolean = false, isReadOnly: boolean = false) => mergeStyleSets({
    rootContainer: {
        '.ac-pushButton': {
            marginTop: "1rem",
            background: 'var(--mgt-theme-brand-background)',
            color: (isDisabled || isReadOnly) ? 'var(--mgt-theme-foreground-disabled) !important' : 'white',
            fontWeight: 400,
            borderColor: "transparent",
            borderRadius: "4px",
            ':hover, :active, :focus': (isDisabled || isReadOnly) ? {
              cursor: 'not-allowed',
            } : {
              background: 'var(--mgt-theme-background-brand-hover) !important'
            }
        },
        '.ac-pushButton-white': {
            background: 'var(--mgt-theme-background)',
            color: (isDisabled || isReadOnly) ? 'var(--mgt-theme-foreground-disabled) !important' : 'var(--mgt-theme-brand-foreground)',
            fontWeight: 400,
            borderColor: "var(--mgt-theme-border-color)",
            borderRadius: "4px",
            ':hover, :active, :focus': (isDisabled || isReadOnly) ? {
              cursor: 'not-allowed',
            } : {
              background: 'var(--mgt-theme-background-hover) !important'
            }
        },
        '.ac-input.ac-textInput, .ac-input.ac-numberInput, .ac-input.ac-dateInput, .ac-input.ac-timeInput, .ac-input.ac-multichoiceInput': {
            ':focus, :focus-visible': {
                outlineColor: "#d1d1d1 !important",
            },
        },
        '.ac-richTextBlock > span:first-of-type': {
            color: 'var(--mgt-theme-foreground) !important'
        },
        '.ac-richTextBlock > span:nth-of-type(2)': {
            color: 'red !important'
        },
        '.ac-textBlock': {
            color: "var(--mgt-theme-foreground) !important"
        },
        '.ms-Button-label': {
            fontSize: "14px",
            fontWeight: "400"
        },
        '.ms-Button': {
            borderRadius: '4px'
        }
    }
});

const dropDownClassesDefault = mergeStyleSets({
  rootContainer: {
    '.tikit-input, .ac-input.ac-multichoiceInput.ac-choiceSetInput-compact': {
      background: 'white',
      color: 'black !important'
    },
    '.tikit-typeahead':{
      '--selected-item-background-color': 'var(--mgt-theme-background2)',
      '--selected-item-background-image': "url('data:image/svg+xml, <svg width=\"16\" height=\"16\" viewBox=\"2 2 16 16\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M4.08859 4.21569L4.14645 4.14645C4.32001 3.97288 4.58944 3.9536 4.78431 4.08859L4.85355 4.14645L10 9.293L15.1464 4.14645C15.32 3.97288 15.5894 3.9536 15.7843 4.08859L15.8536 4.14645C16.0271 4.32001 16.0464 4.58944 15.9114 4.78431L15.8536 4.85355L10.707 10L15.8536 15.1464C16.0271 15.32 16.0464 15.5894 15.9114 15.7843L15.8536 15.8536C15.68 16.0271 15.4106 16.0464 15.2157 15.9114L15.1464 15.8536L10 10.707L4.85355 15.8536C4.67999 16.0271 4.41056 16.0464 4.21569 15.9114L4.14645 15.8536C3.97288 15.68 3.9536 15.4106 4.08859 15.2157L4.14645 15.1464L9.293 10L4.14645 4.85355C3.97288 4.67999 3.9536 4.41056 4.08859 4.21569L4.14645 4.14645L4.08859 4.21569Z\" /></svg>')",
      '--selected-item-background-imageSize': "14px 14px",
      '--selected-item-hover-color': 'var(--mgt-theme-background-brand-hover1)',
      '--selected-container-background-color': 'var(--mgt-theme-background)'
    }
  }
});

const dropDownClassesDark = mergeStyleSets({
  rootContainer: {
    '.tikit-input, .ac-input.ac-textInput, .ac-input.ac-numberInput, .ac-input.ac-dateInput, .ac-input.ac-timeInput, .ac-input.ac-multichoiceInput, .ac-input.ac-multichoiceInput.ac-choiceSetInput-compact, .ac-input.ac-choiceSetInput-expanded, .ac-input.ac-choiceSetInput-multiSelect': {
      background: 'var(--mgt-theme-background2) !important',
      color: 'var(--mgt-theme-foreground) !important',
      'color-scheme': "dark"
    },
    '.tikit-input': {
        borderColor: 'var(--mgt-theme-foreground) !important'
    },
    '.tikit-cardPicker':{
      '--input-background-color': 'var(--mgt-theme-background2)',
      '--selected-person-background-color': 'var(--mgt-theme-background)'
    },
    '.tikit-typeahead':{
      '--selected-item-background-color': 'var(--mgt-theme-background)',
      '--selected-item-background-image': "url('data:image/svg+xml, <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 2048 2048\" class=\"svg_dd790ee3\" focusable=\"false\" fill=\"none\"><path d=\"M1169 1024l879 879-145 145-879-879-879 879L0 1903l879-879L0 145 145 0l879 879L1903 0l145 145-879 879z\" fill=\"white\"></path></svg>')",
      '--selected-item-background-imageSize': "11px",
      '--selected-item-hover-color': 'var(--mgt-theme-background-brand-hover1)',
      '--selected-container-background-color': 'var(--mgt-theme-background2)'
    }
  }
});

export interface AdaptiveCardUploadProps {
  currentFiles: FileProps[];
  onOpenUploadFromCardAction: () => void;
  message?: string;
  messageIsPrivate?: boolean;
  key: string;
}

interface Props {
    keyString?: string;
    jsonString: string, 
    cardProps?: CardProps,
    containerClassName?: string,
    clear?:boolean,
    onLoad?:(updatedInputs?: any)=>void;
    onInputValueChanged?: (input: Input) => void;
    onExecuteAction?: (action: Action) => void;
    uploadFileProps?: AdaptiveCardUploadProps;
    requestMappedText?: string;
    isDisabled?: boolean;
    showDefaultCardButtons?: boolean;
    applyCustomVisibility?: boolean;
    isEndUserPage?: boolean;
    isReadOnly?: boolean;
}

export const AdaptiveCardElement = (props: Props) => {
    const api = new platformService();
    const graphAPI = new graphService();
    const setAppState = useSetState();
    let currentState = appState();
    let FAILEDAPICOUNT = 0;
    const { jsonString, cardProps, containerClassName, onLoad, onInputValueChanged, onExecuteAction, clear, uploadFileProps, requestMappedText, isDisabled, showDefaultCardButtons, applyCustomVisibility, isEndUserPage, isReadOnly } = props;
    const ref = useRef<HTMLDivElement>(null);
    const [features1, setFeatures1] = useState<ACFeatureStates>();
    const [onLoadUpdatedInputs, setOnLoadUpdatedInputs] = useState({});

    const loadFeatureVarsData = useCallback(async () => {
      const featureResult = await loadFeatureVars();
      setFeatures1(featureResult);
    }, []);

    const getAdaptiveCard = (cardJson: any) => {   

        if(features1)
          registerCardCustomComponents(features1, currentState);

        if(cardJson.body){
          const params = {
            container: cardJson.body,
            currentState: currentState, 
            onInputValueChanged: onInputValueChanged, 
            uploadFileProps: uploadFileProps, 
            requestMappedText: requestMappedText, 
            isDisabled: isDisabled || isReadOnly,
            setOnLoadUpdatedInputs: setOnLoadUpdatedInputs, 
            showDefaultCardButtons: showDefaultCardButtons,
            applyCustomVisibility: applyCustomVisibility,
            isEndUserPage: isEndUserPage
          } as CreateCustomElementsProps;
          createCustomElements(params);
        }

        AdaptiveCards.AdaptiveCard.onProcessMarkdown = (text, result) => {
            result.outputHtml = new markdownit().render(text);
            result.didProcess = true;
        }

        // Create an AdaptiveCard instance
        var adaptiveCard = new AdaptiveCards.AdaptiveCard();

        // Set its hostConfig property unless you want to use the default Host Config
        // Host Config defines the style and behavior of a card
        adaptiveCard.hostConfig = new AdaptiveCards.HostConfig({
            fontFamily: "Segoe UI, Helvetica Neue, sans-serif"
            // More host config options
        });

        // Parse the card payload
        adaptiveCard.parse(cardJson);

        adaptiveCard.onInputValueChanged = (input: Input) => {
            onInputValueChanged && onInputValueChanged(input);
        }

        adaptiveCard.onExecuteAction = (action: Action) => {
            onExecuteAction && onExecuteAction(action);
        }

        // Render the card to an HTML element:
        var renderedCard = adaptiveCard.render();

        return renderedCard;
    }

    const hideExecuteButton = () => {
      const parent = document.querySelector(".AdaptiveCardElementValidationClass");
      if(!parent) return;
      const fields = parent.querySelectorAll(".ac-pushButton");
      if (fields.length > 0) {
        const btn = fields[fields.length - 1] as HTMLButtonElement;
        btn.hidden = true;
      }
    }

    const loadCardData = () => {
      const cardJson = jsonString.length > 0 ? JSON.parse(jsonString) : blankPayloadData;
                
      if (clear!=undefined && clear) {
        UploadFile.clear = true;
        ref.current.innerHTML="";
      }
      
      let currentCards = ref.current.getElementsByClassName("ac-adaptiveCard");
      if (currentCards.length > 0) {
        Array.from(currentCards).forEach((card) => {
          card.remove();
        });
      }

      if (cardJson) {
        ref.current.appendChild(getAdaptiveCard(cardJson));
      }

      if (onLoad) {
        onLoad(onLoadUpdatedInputs);
      }

      setOnLoadUpdatedInputs({});

      if (isReadOnly) {
        ref.current.querySelectorAll('textarea').forEach(ta => ta.readOnly = true)
        ref.current.querySelectorAll('select').forEach(s => s.disabled = true);
        ref.current.querySelectorAll('input').forEach(i => i.disabled = true)
        ref.current.querySelectorAll('button').forEach(b => b.disabled = true)
      }

      hideExecuteButton();
    }

    useEffect(() => {
      if(features1 && ref.current)
        loadCardData();
    }, [ref, jsonString, features1, uploadFileProps?.currentFiles]);
    
    useEffect(() => {
        if((currentState?.usersInRole?.length ?? 0) == 0){
          ReloadEndUserCFOptions(api, graphAPI, currentState, setAppState).finally(() => {
            if(!features1)
              loadFeatureVarsData();
          });
        }else if(!features1)
          loadFeatureVarsData();
    }, [currentState?.usersInRole, currentState?.teams]);

    const classNames = getClassNames(isDisabled, isReadOnly)
    
    return (
        <FluentUIThemeConsumer
        render={(globalTheme) => {
        return (
          <>
            <Card key={props.keyString}
              fluid
              elevated
              style={{
                padding: 0,
                borderTop: `2px solid ${globalTheme.siteVariables.colorScheme.brand.foreground}`,
                ...ThemeColorScheme(globalTheme.siteVariables)
              }}
              {...cardProps}
            >
              <Card.Body fitted>
                <Flex column gap="gap.small">
                  <div
                    ref={ref}
                    className={`${containerClassName} ${
                      classNames.rootContainer
                    } ${
                      globalTheme.siteVariables.theme === themeNames.Dark
                        ? dropDownClassesDark.rootContainer
                        : dropDownClassesDefault.rootContainer
                    }`}
                  ></div>
                </Flex>
              </Card.Body>
            </Card>
          </>
        );}}
        />
    )
}